/**
 * Travel Guide Presenter Component
 * Presentational component for travel guide tab content
 */

import React from 'react';
import { spacing } from '@/design-system/utils';
import { QuickPeekResponse } from '@/services/api/interfaces/ITravelAssistantRepository';
import { ChatLocation } from '@/types/chatTypes';
import { TravelGuideLoader } from './TravelGuideLoader';
import { BestTimeToVisitSection } from './BestTimeToVisitSection';
import { ModeOfTravelSection } from './ModeOfTravelSection';
import { AccessibilitySection } from './AccessibilitySection';
import { TerrainTypeSection } from './TerrainTypeSection';
import { HistoricalSignificanceSection } from './historical-significance';
import { FoodSpecialtiesSection } from './food-specialties';
import { LocalLanguagesSection } from './local-languages';
import { LocalExperiencesSection } from './local-experiences';
import { HandicraftsAndSouvenirsSection } from './handicrafts-souvenirs';
import { CulturalAspectsSection } from './cultural-aspects';
import { ActivitiesAndAttractionsSection } from './ActivitiesAndAttractionsSection';
import { AmenitiesAndFacilitiesSection } from './AmenitiesAndFacilitiesSection';
import { SafetyAndSecuritySection } from './SafetyAndSecuritySection';
import { NavigationDifficultySection } from './navigation-difficulty';
import { getTravelIcon } from '../place-overlay/utils/iconUtils';
import { getTravelModeIconName } from '@/utils/iconMapping';
import { deduplicateItems, deduplicateStrings } from './utils/deduplication';

export interface TravelGuidePresenterProps {
  quickPeek: QuickPeekResponse | null;
  isLoading: boolean;
  isLoadingTravelPlanning?: boolean;
  isLoadingTravelPlanningOnly?: boolean;
  isLoadingNavigationAccessibility?: boolean;
  isLoadingCulturalInfo?: boolean;
  isLoadingActivitiesFacilities?: boolean;
  placeName: string | null;
  location?: ChatLocation;
}

export const TravelGuidePresenter: React.FC<TravelGuidePresenterProps> = ({
  quickPeek,
  isLoading,
  isLoadingTravelPlanning = false,
  isLoadingTravelPlanningOnly = false,
  isLoadingNavigationAccessibility = false,
  isLoadingCulturalInfo = false,
  isLoadingActivitiesFacilities = false,
  placeName,
  location,
}) => {
  console.log('🎨 [TravelGuidePresenter] 🎬 RENDER:', {
    isLoading,
    hasQuickPeek: !!quickPeek,
    quickPeekType: typeof quickPeek,
    quickPeekKeys: quickPeek ? Object.keys(quickPeek) : [],
    placeName,
    willShowLoader: isLoading,
    willShowContent: !isLoading && !!quickPeek,
    willReturnNull: !isLoading && !quickPeek,
    timestamp: new Date().toISOString(),
  });

  if (isLoading) {
    console.log(
      '⏳ [TravelGuidePresenter] Showing loader because isLoading=true'
    );
    return <TravelGuideLoader />;
  }

  if (!quickPeek) {
    console.log('❌ [TravelGuidePresenter] Returning null because !quickPeek', {
      isLoading,
      placeName,
      timestamp: new Date().toISOString(),
    });
    // Don't return null - show empty state or message instead
    // Returning null can cause layout shifts
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        No travel guide data available for this location.
      </div>
    );
  }

  console.log(
    '✅ [TravelGuidePresenter] Rendering content with quickPeek data:',
    {
      hasBestTimeToVisit: !!quickPeek.bestTimeToVisit,
      travelModesCount: quickPeek.travelModes?.length || 0,
      hasAccessibility: !!quickPeek.accessibility,
      terrainTypesCount: quickPeek.terrainTypes?.length || 0,
      hasNavigationDifficulty: !!quickPeek.navigationDifficulty,
      hasHistoricalSignificance: !!quickPeek.historicalSignificance,
      hasCulturalAspects: !!quickPeek.culturalAspects,
      foodSpecialtiesCount: quickPeek.foodSpecialties?.length || 0,
      localLanguagesCount: quickPeek.localLanguages?.length || 0,
      localExperiencesCount: quickPeek.localExperiences?.length || 0,
      handicraftsCount: quickPeek.handicraftsAndSouvenirs?.length || 0,
      hasActivities: !!quickPeek.activities,
      hasAmenities: !!quickPeek.amenitiesAndFacilities,
      hasSafety: !!quickPeek.safetyAndSecurity,
    }
  );

  // Loading placeholder component for sections
  const SectionLoadingPlaceholder: React.FC<{ title?: string }> = ({
    title,
  }) => (
    <div
      style={{
        backgroundColor: '#F5F5F5',
        borderRadius: '8px',
        padding: spacing[4],
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[3],
      }}
    >
      {title && (
        <div
          style={{
            width: '200px',
            height: '24px',
            backgroundColor: '#E7E5E4',
            borderRadius: '4px',
          }}
          className='animate-pulse'
        />
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: spacing[2],
        }}
      >
        {[1, 2, 3].map(i => (
          <div
            key={i}
            style={{
              width: `${90 - i * 5}%`,
              height: '16px',
              backgroundColor: '#E7E5E4',
              borderRadius: '4px',
            }}
            className='animate-pulse'
          />
        ))}
      </div>
    </div>
  );

  // Use a more stable loading approach that respects sequential API order
  // Only show sections when their specific API and all previous APIs have completed
  // Travel planning is split into two APIs, show in order: travel-planning-only first, then navigation-accessibility
  const isTravelPlanningLoading =
    isLoadingTravelPlanningOnly || isLoadingNavigationAccessibility;
  const canShowTravelPlanningOnly = !isLoadingTravelPlanningOnly;
  const canShowNavigationAccessibility =
    canShowTravelPlanningOnly && !isLoadingNavigationAccessibility;
  const canShowTravelPlanning = canShowNavigationAccessibility;
  const canShowCulturalInfo = canShowTravelPlanning && !isLoadingCulturalInfo;
  const canShowActivities =
    canShowCulturalInfo && !isLoadingActivitiesFacilities;

  // Debug logging to track loading states
  console.log(' [TravelGuidePresenter] Loading states:', {
    isLoadingTravelPlanning,
    isLoadingTravelPlanningOnly,
    isLoadingNavigationAccessibility,
    isLoadingCulturalInfo,
    isLoadingActivitiesFacilities,
    canShowTravelPlanningOnly,
    canShowNavigationAccessibility,
    canShowTravelPlanning,
    canShowCulturalInfo,
    canShowActivities,
    timestamp: new Date().toISOString(),
  });

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: spacing[4],
        width: '100%',
      }}
    >
      {/* Travel Planning Section - Display in order: travel-planning-only first, then navigation-accessibility */}
      {isTravelPlanningLoading ? (
        <>
          {/* Show travel-planning-only loading first */}
          {isLoadingTravelPlanningOnly && (
            <>
              <SectionLoadingPlaceholder title='Best Time to Visit' />
              <SectionLoadingPlaceholder title='Mode of Travel' />
            </>
          )}
          {/* Show navigation-accessibility loading after travel-planning-only completes */}
          {canShowTravelPlanningOnly && isLoadingNavigationAccessibility && (
            <>
              <SectionLoadingPlaceholder title='Accessibility' />
              <SectionLoadingPlaceholder title='Terrain Types' />
              <SectionLoadingPlaceholder title='Navigation Difficulty' />
            </>
          )}
        </>
      ) : (
        <>
          {/* Best Time to Visit - from API */}
          {quickPeek?.bestTimeToVisit && (
            <BestTimeToVisitSection
              bestTimeToVisit={quickPeek.bestTimeToVisit}
              placeName={placeName}
            />
          )}

          {/* Mode of Travel - from API */}
          {quickPeek?.travelModes && quickPeek.travelModes.length > 0 && (
            <ModeOfTravelSection
              modes={deduplicateItems(
                quickPeek.travelModes.map((mode, index) => {
                  // Map travel mode ID to icon name if icon is not provided
                  // Priority: 1) API icon (URL or name), 2) ID mapping, 3) Label mapping, 4) Empty string (no icon)
                  // Supports dynamic travel modes from backend (metro, taxi, etc.)
                  const iconName =
                    mode.icon ||
                    getTravelModeIconName(mode.id) ||
                    getTravelModeIconName(mode.label) ||
                    '';
                  // Ensure unique ID by combining mode.id with index and placeName
                  const uniqueId = mode.id
                    ? `${mode.id}-${placeName || 'default'}-${index}`
                    : `mode-${placeName || 'default'}-${index}`;
                  return {
                    id: uniqueId,
                    icon: iconName,
                    label: mode.label,
                  };
                }),
                mode => mode.label
              )}
              description={quickPeek.travelModes
                .map(m => {
                  // Format: "Label: description (fare)" to match component's parsing logic
                  let desc = `${m.label}: ${m.description}`;
                  if (m.fare) {
                    desc += ` (${m.fare})`;
                  }
                  return desc;
                })
                .join('\n\n')}
              getIcon={getTravelIcon}
            />
          )}

          {/* Accessibility - from API */}
          {quickPeek?.accessibility && (
            <AccessibilitySection
              items={deduplicateItems(
                quickPeek.accessibility.items.map(item => ({
                  id: item.id,
                  heading: item.heading,
                  description: item.description,
                  expanded: item.expanded,
                })),
                item => item.heading
              )}
            />
          )}

          {/* Terrain Types - from API */}
          {quickPeek?.terrainTypes && quickPeek.terrainTypes.length > 0 && (
            <TerrainTypeSection
              terrainTypes={quickPeek.terrainTypes.map(terrain => ({
                id: terrain.id,
                title: terrain.title,
                image: terrain.image || null,
                description: terrain.description,
              }))}
            />
          )}

          {/* Navigation Difficulty - from API */}
          {(() => {
            const navDifficulty = quickPeek?.navigationDifficulty;
            if (!navDifficulty) return null;

            // Check if it's an object with items array
            if (typeof navDifficulty === 'object') {
              const navObj = navDifficulty as {
                items?: unknown;
                title?: string;
              };
              if ('items' in navObj && Array.isArray(navObj.items)) {
                return (
                  <NavigationDifficultySection
                    title={navObj.title || 'Navigation Difficulty'}
                    items={deduplicateStrings(navObj.items as string[])}
                  />
                );
              }
            }

            // Check if it's a string
            if (
              typeof navDifficulty === 'string' &&
              navDifficulty.trim() !== ''
            ) {
              return (
                <NavigationDifficultySection
                  title='Navigation Difficulty'
                  items={[navDifficulty]}
                />
              );
            }

            return null;
          })()}
        </>
      )}

      {/* Cultural Info Section */}
      {!canShowCulturalInfo ? (
        <>
          {/* Only show cultural info loading when travel planning is complete */}
          {canShowTravelPlanning && (
            <>
              <SectionLoadingPlaceholder title='Handicrafts and Souvenirs' />
              <SectionLoadingPlaceholder title='Historical Significance' />
              <SectionLoadingPlaceholder title='Cultural Aspects' />
              <SectionLoadingPlaceholder title='Food Specialties' />
              <SectionLoadingPlaceholder title='Local Languages' />
              <SectionLoadingPlaceholder title='Local Experiences' />
            </>
          )}
        </>
      ) : (
        <>
          {/* Handicrafts and Souvenirs - from API */}
          {(() => {
            const handicrafts = quickPeek?.handicraftsAndSouvenirs;
            if (
              handicrafts &&
              Array.isArray(handicrafts) &&
              handicrafts.length > 0
            ) {
              console.log(
                '🎨 [TravelGuidePresenter] Rendering handicraftsAndSouvenirs:',
                {
                  count: handicrafts.length,
                  items: handicrafts,
                }
              );
              return (
                <HandicraftsAndSouvenirsSection
                  items={deduplicateItems(
                    handicrafts.map(item => {
                      // Extract image URL - handle both string and object formats
                      let imageUrl: string | null = null;
                      if (item.image) {
                        if (typeof item.image === 'string') {
                          imageUrl = item.image;
                        } else if (
                          typeof item.image === 'object' &&
                          item.image !== null &&
                          'url' in item.image
                        ) {
                          imageUrl = (item.image as { url: string }).url;
                        }
                      }
                      return {
                        id: item.id,
                        title: item.title,
                        description: item.description,
                        image: imageUrl,
                      };
                    }),
                    item => item.title // Deduplicate by title
                  )}
                  placeName={location?.title || location?.place}
                />
              );
            }
            console.log(
              '⚠️ [TravelGuidePresenter] handicraftsAndSouvenirs not rendering:',
              {
                exists: !!quickPeek?.handicraftsAndSouvenirs,
                isArray: Array.isArray(quickPeek?.handicraftsAndSouvenirs),
                length: quickPeek?.handicraftsAndSouvenirs?.length,
                value: quickPeek?.handicraftsAndSouvenirs,
              }
            );
            return null;
          })()}

          {/* Historical Significance - from API */}
          {(() => {
            const historical = quickPeek?.historicalSignificance;
            if (
              !historical ||
              !historical.items ||
              historical.items.length === 0
            ) {
              return null;
            }

            // Handle structured format: Array<{id, title, description}>
            if (
              Array.isArray(historical.items) &&
              historical.items.length > 0 &&
              typeof historical.items[0] === 'object' &&
              'title' in historical.items[0]
            ) {
              return (
                <HistoricalSignificanceSection
                  title={historical.title}
                  items={(
                    historical.items as Array<{
                      id: string;
                      title: string;
                      description: string;
                    }>
                  ).map(item => ({
                    id: item.id,
                    heading: item.title,
                    description: item.description,
                  }))}
                  image={historical.imageUrls?.[0] ?? historical.image}
                />
              );
            }

            // Handle string array format (legacy)
            return (
              <HistoricalSignificanceSection
                title={historical.title}
                items={historical.items as string[]}
                image={historical.imageUrls?.[0] ?? historical.image}
              />
            );
          })()}

          {/* Cultural Aspects - from API */}
          {(() => {
            const cultural = quickPeek?.culturalAspects;
            if (!cultural || !cultural.items || cultural.items.length === 0) {
              return null;
            }

            // Handle structured format: Array<{id, title, description}>
            if (
              Array.isArray(cultural.items) &&
              cultural.items.length > 0 &&
              typeof cultural.items[0] === 'object' &&
              'title' in cultural.items[0]
            ) {
              return (
                <CulturalAspectsSection
                  title={cultural.title}
                  items={deduplicateItems(
                    (
                      cultural.items as Array<{
                        id: string;
                        title: string;
                        description: string;
                      }>
                    ).map(item => ({
                      id: item.id,
                      heading: item.title,
                      description: item.description,
                    })),
                    item => item.heading // Deduplicate by heading/title
                  )}
                />
              );
            }

            // Handle string array format (legacy)
            return (
              <CulturalAspectsSection
                title={cultural.title}
                items={deduplicateStrings(cultural.items as string[])} // Deduplicate strings
              />
            );
          })()}

          {/* Food Specialties - from API */}
          {quickPeek?.foodSpecialties &&
            quickPeek.foodSpecialties.length > 0 && (
              <FoodSpecialtiesSection
                items={deduplicateItems(
                  quickPeek.foodSpecialties.map(food => {
                    // Extract image URL - handle both string and object formats
                    let imageUrl: string | null = null;
                    if (food.image) {
                      if (typeof food.image === 'string') {
                        imageUrl = food.image;
                      } else if (
                        typeof food.image === 'object' &&
                        food.image !== null &&
                        'url' in food.image
                      ) {
                        imageUrl = (food.image as { url: string }).url;
                      }
                    }

                    console.log('🍽️ [TravelGuidePresenter] Food Specialty:', {
                      id: food.id,
                      name: food.name,
                      originalImage: food.image,
                      extractedImageUrl: imageUrl,
                      description: food.description?.substring(0, 50) + '...',
                    });

                    return {
                      id: food.id,
                      name: food.name,
                      description: food.description,
                      image: imageUrl,
                    };
                  }),
                  item => item.name // Deduplicate by name
                )}
                placeName={location?.title || location?.place}
              />
            )}

          {/* Local Languages - from API */}
          {quickPeek?.localLanguages &&
            Array.isArray(quickPeek.localLanguages) &&
            quickPeek.localLanguages.length > 0 && (
              <LocalLanguagesSection
                items={deduplicateItems(
                  quickPeek.localLanguages
                    .filter(
                      lang =>
                        lang &&
                        lang.name &&
                        lang.description &&
                        lang.name.trim() !== '' &&
                        lang.description.trim() !== ''
                    )
                    .map((lang, index) => ({
                      id: lang.id || `lang-${index}`,
                      name: lang.name || '',
                      description: lang.description || '',
                    })),
                  lang => lang.name
                )}
              />
            )}

          {/* Local Experiences - from API */}
          {quickPeek?.localExperiences &&
            quickPeek.localExperiences.length > 0 && (
              <LocalExperiencesSection
                items={deduplicateItems(
                  quickPeek.localExperiences.map(exp => {
                    // Extract image URL - handle both string and object formats
                    let imageUrl: string | null = null;
                    if (exp.image) {
                      if (typeof exp.image === 'string') {
                        imageUrl = exp.image;
                      } else if (
                        typeof exp.image === 'object' &&
                        exp.image !== null &&
                        'url' in exp.image
                      ) {
                        imageUrl = (exp.image as { url: string }).url;
                      }
                    }
                    return {
                      id: exp.id,
                      title: exp.title,
                      description: exp.description,
                      price: exp.price,
                      image: imageUrl,
                    };
                  }),
                  item => item.title // Deduplicate by title
                )}
                placeName={location?.title || location?.place}
              />
            )}
        </>
      )}

      {/* Activities & Facilities Section */}
      {!canShowActivities ? (
        <>
          {/* Only show activities loading when cultural info is complete */}
          {canShowCulturalInfo && (
            <>
              <SectionLoadingPlaceholder title='Activities & Attractions' />
              <SectionLoadingPlaceholder title='Amenities & Facilities' />
              <SectionLoadingPlaceholder title='Safety & Security' />
            </>
          )}
        </>
      ) : (
        <>
          {/* Activities and Attractions - from API */}
          {quickPeek?.activities &&
            quickPeek.activities.items &&
            quickPeek.activities.items.length > 0 && (
              <ActivitiesAndAttractionsSection
                title={'Activities & Attractions'}
                activities={deduplicateItems(
                  quickPeek.activities.items,
                  item => item.label
                )}
              />
            )}

          {/* Amenities & Facilities - from API */}
          {quickPeek?.amenitiesAndFacilities &&
            quickPeek.amenitiesAndFacilities.items &&
            quickPeek.amenitiesAndFacilities.items.length > 0 && (
              <AmenitiesAndFacilitiesSection
                title={quickPeek.amenitiesAndFacilities.title}
                items={deduplicateItems(
                  quickPeek.amenitiesAndFacilities.items,
                  item => item.label
                )}
              />
            )}

          {/* Safety & Security - from API */}
          {quickPeek?.safetyAndSecurity && (
            <SafetyAndSecuritySection
              title={quickPeek.safetyAndSecurity.title}
              sections={quickPeek.safetyAndSecurity.sections.map(section => ({
                ...section,
                items: deduplicateStrings(section.items),
              }))}
            />
          )}
        </>
      )}
    </div>
  );
};
